import { BuscaCidadaoListagemProblemasFragment, GrupoCondicaoEnum, SituacaoProblema } from 'graphql/types.generated'
import { isUndefinedOrNull } from 'util/checks'
import { formatDate } from 'util/date/formatDate'
import { formatNumber } from 'util/number'

import {
  BuscaCidadaoQueryResultModel,
  CidadaoListagemDiabetesQueryModel,
  CidadaoListagemHipertensaoQueryModel,
} from '../../model/lista-tematica/model-acompanhamentoCondicaoSaudeListaTematica'

type CidadaoListagemQueryModel = CidadaoListagemDiabetesQueryModel | CidadaoListagemHipertensaoQueryModel
type BuscaCidadaoListagemProblema = BuscaCidadaoListagemProblemasFragment['problemas'][0]

const SEM_REGISTRO_MEDICAO = 'Sem registro de medição'

interface Medicao {
  valor?: string
  data?: string
  label?: string
}

const hasRegistro = ({ data }: Medicao): boolean => !isUndefinedOrNull(data)

const isSoap = (medicao: Medicao): boolean => hasRegistro(medicao) && !isUndefinedOrNull(medicao.valor)

const formatValorNumerico = (valor: string, unidade: string): string => `${formatNumber(Number(valor), 2)}${unidade}`

const formatRegistroSigtap = ({ data, label }: Medicao): string => {
  const labelRegistro = label ? ` de ${label}` : ''

  return `Registro${labelRegistro} via SIGTAP em ${formatDate(data)}`
}

const formatMedicao = (medicao: Medicao): string | null => {
  if (!hasRegistro(medicao)) return null

  if (isSoap(medicao)) {
    return `${medicao.valor} em ${formatDate(medicao.data)}`
  }

  return formatRegistroSigtap(medicao)
}

export const formatUltimaMedicaoPesoAltura = (row: BuscaCidadaoQueryResultModel): string => {
  const { ultimaMedicaoPeso, ultimaMedicaoAltura, dataUltimaMedicaoPeso, dataUltimaMedicaoAltura } = row

  const peso: Medicao = {
    valor: !isUndefinedOrNull(ultimaMedicaoPeso) ? formatValorNumerico(ultimaMedicaoPeso, 'kg') : null,
    data: dataUltimaMedicaoPeso,
    label: 'peso',
  }
  const altura: Medicao = {
    valor: !isUndefinedOrNull(ultimaMedicaoAltura) ? formatValorNumerico(ultimaMedicaoAltura, 'cm') : null,
    data: dataUltimaMedicaoAltura,
    label: 'altura',
  }

  if (hasRegistro(peso) && hasRegistro(altura) && peso.data === altura.data) {
    if (isSoap(peso) && isSoap(altura)) {
      return `${peso.valor}/${altura.valor} em ${formatDate(peso.data)}`
    }
    if (!isSoap(peso) && !isSoap(altura)) {
      return `Registro de peso e altura via SIGTAP em ${formatDate(peso.data)}`
    }
  }

  return [formatMedicao(peso), formatMedicao(altura)].filter(Boolean).join(' | ') || SEM_REGISTRO_MEDICAO
}

export const formatUltimaMedicaoPressaoArterial = (row: BuscaCidadaoQueryResultModel): string => {
  const { ultimaMedicaoPressaoArterial, dataUltimaMedicaoPressaoArterial } = row

  return (
    formatMedicao({
      valor: !isUndefinedOrNull(ultimaMedicaoPressaoArterial) ? `${ultimaMedicaoPressaoArterial}mmHg` : null,
      data: dataUltimaMedicaoPressaoArterial,
    }) ?? SEM_REGISTRO_MEDICAO
  )
}

/**
 * @param row
 * @param grupoCondicao
 * @returns `true` se contém algum problema com situação ativa e ciap ou cid do grupo condição informado
 *           ou se nao for da lista tematica correta, `false` caso contrário.
 */
export function hasProblemaAtivoDoGrupoCondicao(
  row: BuscaCidadaoQueryResultModel,
  grupoCondicao: GrupoCondicaoEnum
): boolean {
  if (!grupoCondicao) return true

  const problemas = (row as CidadaoListagemQueryModel).cidadao?.prontuario?.problemas || []
  if (problemas.isEmpty()) return false

  return problemas.some(isProblemaAtivoDoGrupoCondicao(grupoCondicao))
}

const isProblemaAtivoDoGrupoCondicao = (grupoCondicao: GrupoCondicaoEnum) => ({
  ultimaEvolucao,
  ciap,
  cid10,
}: BuscaCidadaoListagemProblema): boolean => {
  const isAtivo = ultimaEvolucao?.situacao === SituacaoProblema.ATIVO
  const isCiapOfGrupoCondicao = ciap?.gruposCondicao?.some(({ grupo }) => grupo === grupoCondicao)
  const isCid10OfGrupoCondicao = cid10?.gruposCondicao?.some(({ grupo }) => grupo === grupoCondicao)

  return isAtivo && (isCiapOfGrupoCondicao || isCid10OfGrupoCondicao)
}
